import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { DropDown, Spinner } from '../../components/common';
import { SectionLabel, ToggleButton } from '../../components/common';
import Header from '../../components/common/global-top-bar';
import SwitchBar from '../../components/switch-bar';
import { Headeroptions, PeerHeaderoptions } from './listener-constants';
import HeadBack from './redirect';
import { connect, useDispatch } from 'react-redux';
import {
  getListenerDetailsRequest,
  getListenerDetailsSuccess,
  getListenerTagsRequest,
  removeListenerTagsRequest,
  updateListenerDetailsRequest,
  updateListenerTagsListRequest,
  updateListenerTagsRequest,
} from './listeners-redux/actions';
import SaveButton from '../clients/switch-bar-button';
import { fetchCommsListRequest } from '../templates/redux-templates/actions';
import Modal from '../../components/common/modal';
import { fetchTosRequest } from '../text-blocks/text-blocks-redux/actions';
import { fetchTiersListRequest } from '../listener-tiers/redux-templates/actions';
import axios from 'axios';
import { fetchTagsGroupListRequest } from '../tags-management/redux-tags/actions';
import { paramsToObjects } from '../../components/common/create-params';
import TierModal from './components/tier-modal';
import EditDetails from './components/profile-edit';
import { ListenerDataType } from '../listener-reviewers/common/types';
import ProfileGradeCard from './common/profile-grade-card';
import { axiosGet } from '../../api/axios';
import { TagKey, useProfileTagGroups } from '../../hooks/useProfileTagGroups';
import { BackgroundTag, PeerType } from '../../hooks/usePeers';
import { useListClients, useDomains } from '../../hooks/useClients';
import { Resource, useResources } from '../../hooks/useResources';
import getUserRoles from '../../utils/getUserRoles';

const mime = require('mime');

type props = {
  Label: string;
  value: string;
  dataTestId: string;
};

type TagDisplay = {
  id: number;
  name: string;
  selected?: boolean;
};

const userStatus = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  under_review: 'Under Review',
  suspended: 'Suspended',
  banned: 'Banned',
};

export const Label: React.FunctionComponent<props> = ({ Label, value, dataTestId }): JSX.Element => {
  return (
    <>
      <label className={`text-gray-dark mb-2 block leading-4`}>{Label}</label>
      <p data-testid={dataTestId} className="text-gray-dark text-sm font-extrabold">
        {value}
      </p>
    </>
  );
};

export enum OnboardingStatus {
  PENDING = 'Pending',
  REVISIT = 'Revisit',
  DONE = 'Done',
  ERROR = 'Error',
}

type ListenerDetailProps = {
  userDataProp: any;
  comms: any;
  pending: boolean;
  tiersList: any;
  tags: any;
  listenersTags: any;
};

export const getListenerType = (state: string) => {
  if ([PeerType.ONBOARDING_PEER, PeerType.ACTIVE_PEER].includes(state)) {
    return 'peer';
  } else if ([PeerType.ACTIVE_LISTENER, PeerType.UPGRADING_PEER].includes(state)) {
    return 'listener';
  } else {
    return 'rejected';
  }
};

const PeerTypeLabels = {
  onboarding_peer: 'Onboarding Peer',
  rejected_peer: 'Rejected Peer',
  active_peer: 'Peer',
  upgrading_peer: 'Peer Listener Onboarding',
  active_listener: 'Peer Listener',
};

const getExperiencesChecklistLabel = (listenerDetails?: {
  content_optional?: boolean;
  has_required_experiences?: boolean;
}): string => {
  if (listenerDetails?.content_optional) return 'Waived';
  if (listenerDetails?.has_required_experiences) return 'Done';
  return 'Pending';
};

type UserTraitTagsTypes = {
  [K in 'pronouns' | 'races' | 'genders' | 'family' | 'spirituality' | 'relationship']: BackgroundTag[];
};

const Profile: React.FunctionComponent<ListenerDetailProps> = ({
  userDataProp,
  pending,
  comms,
  tags,
  listenersTags,
}): JSX.Element => {
  const params = paramsToObjects() || {};
  const listenerId = params['listenerRoleId'];
  const userId = params['listenerId'];
  const listenerType = params['listenerType'];

  const [userDetails, setUserDetails] = useState<any>();
  const [listenerDetails, setListenerDetails] = useState<any>();
  const [enableSaveOption, setEnableSaveOption] = useState(false);

  // Redirect to peer listing if no params
  const history = useHistory();

  useEffect(() => {
    if (!listenerId) {
      history.push('/peers');
    }
  }, [listenerId, history]);

  // Calculate the status of items that need approval. Relates to https://listenersoncall.atlassian.net/browse/LOC-2960
  // we can't calculate profile image status using the calculateApprovalStatus because it's a different logic
  let profileApprovedStatus = calculateProfileImageStatus(userDetails);
  let displayNameStatus = calculateApprovalStatus(
    userDetails?.display_name,
    userDetails?.proposed_display_name,
    userDetails?.display_name_rejected,
  );
  let aboutMeStatus = calculateApprovalStatus(
    userDetails?.listener_role?.about_me,
    userDetails?.listener_role?.proposed_about_me,
    userDetails?.listener_role?.about_me_rejected,
  );

  const [addTagsPopup, setAddTagsPopup] = useState<'PROFILE' | 'SORTING' | ''>('');

  const { getTagsByKey } = useProfileTagGroups();
  const [selectedProfileTags, setSelectedProfileTags] = useState<any>([]);
  const [selectedSortingTags, setSelectedSortingTags] = useState<any>([]);
  const [selectedBackgroundTags, setSelectedBackgroundTags] = useState<any>([]);

  const dispatch = useDispatch();
  const [userTraitTags, setUserTraitTags] = useState<UserTraitTagsTypes>({
    pronouns: [],
    races: [],
    genders: [],
    family: [],
    spirituality: [],
    relationship: [],
  });

  // TODO this should be view=summary to improve performance
  const { data: clients, isFetching: isClientsFetching } = useListClients();
  const { data: domains, isFetching: isDomainsFetching } = useDomains();

  const { data: resources, isFetching: isResourcesFetching } = useResources(false);
  const [clientResources, setClientResources] = useState<Resource[] | null>(null);
  const clientResourceData = useResources(false, undefined, listenerDetails?.client_id || undefined, true);

  useEffect(() => {
    if (clientResourceData.data && clientResourceData.data.length > 0) {
      setClientResources(clientResourceData.data);
    } else {
      setClientResources(null);
    }
  }, [clientResourceData.data]);

  const allResources = [...(resources || []), ...(clientResources || [])];

  // Derive from the unsaved Group Affiliation selection so badge / Experiences chip
  // stay in sync before save (server-computed content_optional*_from_* lag the dropdown).
  const contentOptionalFromClient = Boolean(
    listenerDetails?.client_id &&
      clients?.find((client: any) => Number(client.id) === Number(listenerDetails.client_id))?.content_optional,
  );
  const isContentOptional = Boolean(listenerDetails?.content_optional_from_tier) || contentOptionalFromClient;

  const initStates = useCallback((initalData) => {
    if (initalData) {
      setEnableSaveOption(false);
      setUserDetails(initalData);
      const role = initalData.listener_role;
      // Favor Group Affiliation: if both client_id and domain_id exist, keep only client_id
      const normalizedRole = role?.client_id && role?.domain_id ? { ...role, domain_id: null } : role;
      setListenerDetails(normalizedRole);
    }
  }, []);

  useEffect(() => {
    initStates(userDataProp);
  }, [initStates, userDataProp]);

  const normalizeInput = (value: string): string => {
    if (value) {
      // only allows 0-9 inputs
      const currentValue = value.replace(/[^\d]/g, '');
      const cvLength = currentValue.length;
      if (cvLength > 0) {
        if (cvLength < 4) return currentValue;
        if (cvLength < 7) return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3)}`;
        return `(${currentValue.slice(0, 3)}) ${currentValue.slice(3, 6)}-${currentValue.slice(6, 10)}`;
      }
    }
    return '';
  };

  const setUserValue = (name: string, value: string | number) => {
    setEnableSaveOption(true);
    setUserDetails((prev: any) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    dispatch(getListenerDetailsRequest({ id: userId }));
    dispatch(fetchCommsListRequest({ only_group_key: 'listener_profile_rejected' }));
    dispatch(fetchTosRequest({ type: 'app' }));
    dispatch(fetchTiersListRequest({ all_tier_versions: true }));
    dispatch(
      fetchTagsGroupListRequest({
        keys: 'BACKGROUND,SORTING,PROFILE,PRONOUN,RACE/ETHNICITY,GENDER,RELATIONSHIP,SPIRITUALITY,FAMILY',
      }),
    );
    dispatch(getListenerTagsRequest({ id: listenerId, payload: { include_hidden: true } }));
    return () => {
      dispatch(getListenerDetailsSuccess({ listened: null }));
    };
  }, [userId, dispatch, listenerId]);

  const formatPhone = useCallback(
    (mobilePhone: string) => {
      if (!mobilePhone) {
        return;
      }
      const phone = mobilePhone.replace('+1', '');
      setUserDetails((prev: any) => ({ ...prev, mobile_phone: normalizeInput(phone) }));
    },
    [setUserDetails],
  );

  useEffect(() => {
    formatPhone(userDetails?.mobile_phone);
  }, [userDetails?.mobile_phone, userDetails?.approvedState, userDetails?.profileUrl, formatPhone]);

  // HACKHACKHACK: the existing Redux store doesn't user the listeners /profile API endpoint
  // so we're unable to get the `notes` key from the generic users endpoint, so this is a
  // one-off fix to get the `notes` upon a page load (updating was working fine previously)
  useEffect(() => {
    axiosGet(`/listeners/${listenerId}/profile`, {})
      .then((res) => {
        setListenerDetails((prev: any) => ({
          ...prev,
          // I dunno why it's an array either
          notes: res.data[0].notes,
        }));
      })
      .catch(() => {});
  }, [listenerId]);

  // process user trait tags
  useEffect(() => {
    if (tags && tags.length > 0) {
      const getTags = (type: string) => {
        const filteredTags = tags?.find((tag: any) => tag.name === type);
        return filteredTags ? filteredTags.tags?.map((tag: any) => tag.name) : [];
      };

      setUserTraitTags({
        pronouns: getTags('Pronoun'),
        races: getTags('Race/Ethnicity'),
        genders: getTags('Gender'),
        family: getTags('Family'),
        spirituality: getTags('Spirituality'),
        relationship: getTags('Relationship'),
      });
    }
  }, [tags, setUserTraitTags]);

  const updateTags = useCallback(
    (key: TagKey, selectedTags: BackgroundTag[], setSelectedTags: (tags: BackgroundTag[]) => void) => {
      const tags: TagDisplay[] = getTagsByKey(key) || [];
      const tagIds = tags.map((tag) => tag.id);
      const newTags = listenersTags.filter((tte: BackgroundTag) => tagIds?.includes(tte.tag.id));

      if (key === 'BACKGROUND') {
        const newApprovedTags = newTags.filter((tte: BackgroundTag) => tte.status === 'APPROVED');
        const oldApprovedTags = selectedTags.filter((tte: BackgroundTag) => tte.status === 'APPROVED');
        if (newTags.length !== selectedTags.length || newApprovedTags.length !== oldApprovedTags.length) {
          setSelectedTags(newTags);
        }
      } else if (newTags.length !== selectedTags.length) {
        setSelectedTags(newTags);
      }
    },
    [getTagsByKey, listenersTags],
  );

  useEffect(() => {
    if (listenersTags && listenersTags.length > 0) {
      updateTags('PROFILE', selectedProfileTags, setSelectedProfileTags);
      updateTags('SORTING', selectedSortingTags, setSelectedSortingTags);
      updateTags('BACKGROUND', selectedBackgroundTags, setSelectedBackgroundTags);
    }
  }, [getTagsByKey, listenersTags, selectedBackgroundTags, selectedProfileTags, selectedSortingTags, updateTags]);

  const setToggle = (status: any, id: any) => {
    status
      ? dispatch(
          updateListenerTagsListRequest({
            id: listenerId,
            payload: { id: +userId, tag_id: id },
          }),
        )
      : dispatch(removeListenerTagsRequest({ id: +listenerId, tag_id: id }));
  };

  const handleSave = () => {
    const listenerDetailsPayload: ListenerDataType = {
      ...listenerDetails,
    };
    const omitListenerFields: string[] = [
      'about_me_rejected',
      'proposed_about_me',
      'about_me',
      'is_active',
      'is_peer_active',
    ];
    omitListenerFields.forEach((field: string) => {
      delete listenerDetailsPayload[field as keyof ListenerDataType];
    });

    const userDetailsPayload = {
      status: userDetails.status,
      is_text_compatible_phone: userDetails.is_text_compatible_phone,
      is_mms_compatible_phone: userDetails.is_mms_compatible_phone,
    };

    // We need to check if the resource_id is valid for the client_id
    // If the resource belongs to a client and that client is not affiliated with the listener, we need to nullify the resource_id
    let { resource_id, client_id, domain_id } = listenerDetailsPayload;
    const matchingResource = allResources?.find((resource) => Number(resource.id) === Number(resource_id));
    if (!matchingResource || (matchingResource.client_id && matchingResource.client_id !== client_id)) {
      resource_id = null;
    }

    // Domain and Client affiliations are mutually exclusive; favor Group (client)
    const finalClientId = client_id || null;
    const finalDomainId = client_id ? null : domain_id || null;

    const payload = {
      id: userId,
      listenerId,
      payload: {
        ...userDetailsPayload,
        listener_role: {
          ...listenerDetailsPayload,
          client_id: finalClientId,
          domain_id: finalDomainId,
          resource_id: resource_id || null,
        },
      },
    };

    dispatch(updateListenerDetailsRequest(payload));
  };

  const handleBackGroundToggle = (id: number, tag_id: number, is_approved: boolean) => {
    const payload = {
      id: listenerId,
      tag_id: id,
      payload: {
        tag_id,
        status: is_approved ? 'APPROVED' : 'UNGRADED',
      },
    };
    dispatch(updateListenerTagsRequest(payload));
  };

  const handleDelete = (id: number) => {
    dispatch(removeListenerTagsRequest({ id: listenerId, tag_id: id }));
  };

  const getSectionLabel = (): string => {
    switch (listenerDetails?.state) {
      case PeerType.ACTIVE_PEER:
        return 'Peer';
      case PeerType.REJECTED_PEER:
        return 'Rejected Peer';
      case PeerType.ONBOARDING_PEER:
        return 'Onboarding Peer';
      case PeerType.UPGRADING_PEER:
        return 'Upgrading Peer';
      case PeerType.ACTIVE_LISTENER:
        return 'Peer Listener';
      default:
        return 'Unknown';
    }
  };

  const isPeerListener = (): boolean => {
    return [PeerType.UPGRADING_PEER, PeerType.ACTIVE_LISTENER].includes(listenerDetails?.state);
  };

  const getSelectedTagOptions = (key: 'PROFILE' | 'SORTING') => {
    const tagList: TagDisplay[] | undefined = getTagsByKey(key);

    let selectedTags: BackgroundTag[] = [];
    if (key === 'PROFILE') {
      selectedTags = selectedProfileTags || [];
    } else if (key === 'SORTING') {
      selectedTags = selectedSortingTags || [];
    }

    if (tagList && selectedTags) {
      const selectedTagIds = selectedTags.map((selectedTag: BackgroundTag) => selectedTag.tag.id);
      tagList.forEach((item: TagDisplay) => {
        if (item) {
          item.selected = selectedTagIds.includes(item?.id);
        }
      });
    }
    return tagList;
  };

  return (
    <div>
      {pending && <Spinner />}
      <Header heading={<HeadBack listenerName={userDetails?.first_name || ''} listenerId={Number(listenerId)} />} />
      <SwitchBar
        heading={listenerType === 'peer' ? PeerHeaderoptions : Headeroptions}
        position={0}
        forwardParams
        optionalButton={
          <div className={`w-1/12 ${!enableSaveOption ? 'cursor-not-allowed' : ''} `}>
            <SaveButton onSave={handleSave} disable={!enableSaveOption} />
          </div>
        }
      />
      <div className="max-window-height flex justify-between overflow-y-auto">
        <div className="w-1/2">
          <div className="px-7">
            <SectionLabel Label={getSectionLabel()} />
          </div>
          <ProfileGradeCard
            currentPhotoUrl={listenerDetails?.current_profile_photo?.square_url}
            proposedPhotoUrl={
              listenerDetails?.proposed_profile_photo?.status != 'rejected'
                ? listenerDetails?.proposed_profile_photo?.square_url
                : null
            }
            profilePicVerified={!!listenerDetails?.current_profile_photo}
            lastName={userDetails?.last_name || ''}
            firstName={userDetails?.first_name || ''}
            comms={comms}
            peerState={listenerDetails?.state}
            listenerId={listenerDetails?.id}
            data-testid="profile-photo"
          />
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label value={userDetails?.id} Label={'User ID#'} dataTestId="user-id" />
            </div>
            <div className="flex w-1/2 justify-between">
              {listenerDetails?.is_peer_active && (
                <a
                  className="bg-blue-primary flex h-10 w-36 items-center justify-center rounded-sm px-2 text-sm text-white"
                  href={`${process.env.REACT_APP_MWA_LINK}/peer-details?listenerId=${listenerDetails.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Peer MWA Page
                </a>
              )}
              <EditDetails tags={userTraitTags} />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label value={getUserRoles(userDataProp)} Label="Roles" dataTestId="roles" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label value={userDetails?.first_name} Label={'First Name'} dataTestId="first-name" />
            </div>

            <div className="w-1/2 px-2">
              <Label value={userDetails?.last_name} Label={'Last Name'} dataTestId="last-name" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-full px-2">
              <Label value={userDetails?.display_name} Label={'Display Name'} dataTestId="display-name" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-full px-2">
              <Label value={userDetails?.email_address} Label={'Email'} dataTestId="email" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label value={userDetails?.mobile_phone} Label={'Phone'} dataTestId="phone" />
            </div>
            <div className="w-1/2 px-2">
              <Label Label="Time Zone" value={userDetails?.timezone} dataTestId="timezone" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label value={userDetails?.date_of_birth} Label={'DOB'} dataTestId="dob" />
            </div>
            <div className="w-1/2 px-2">
              <Label Label={'Gender'} value={userDetails?.gender} dataTestId="gender" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label
                Label={'Primary Language'}
                value={listenerDetails?.primary_language}
                dataTestId="primary-language"
              />
            </div>
            <div className="w-1/2 px-2">
              <Label Label={'Pronouns'} value={userDetails?.pronoun} dataTestId="pronouns" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label Label={'Race'} value={userDetails?.race} dataTestId="race" />
            </div>
            <div className="w-1/2 px-2">
              <Label Label={'Spirituality'} value={userDetails?.spirituality} dataTestId="spirituality" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label Label={'Relationship'} value={userDetails?.relationship} dataTestId="relationship" />
            </div>
            <div className="w-1/2 px-2">
              <Label Label={'Family'} value={userDetails?.family} dataTestId="family" />
            </div>
          </div>
          <div className="flex justify-between px-7 py-4">
            <div className="w-1/2 px-2">
              <Label Label={'How Did You Hear?'} value={userDetails?.how_did_you_hear} dataTestId="how-did-you-hear" />
            </div>
          </div>
        </div>
        <div className="w-1/2">
          <div>
            <div className="px-7">
              <div className="flex w-full justify-between py-2 pr-2">
                <TierModal />
              </div>
              <div className="gray-border-line w-full border-b">
                <div className="flow-root py-2">
                  <h3 className="text-md text-gray-dark float-left">
                    Member Visibility ({listenerDetails?.peer_status === 'enabled' ? 'Auto' : 'Admin'}):
                  </h3>
                  <div className="float-right">
                    <div
                      className={`${
                        listenerDetails?.is_peer_active ? 'bg-citrus' : 'bg-redSalsa'
                      } rounded-md px-4 py-1 text-base font-bold text-white`}
                      data-testid="status"
                    >
                      {listenerDetails?.is_peer_active ? 'Visible' : 'Hidden'}
                      {listenerDetails?.can_take_calls
                        ? '/' + (listenerDetails?.is_active ? 'Callable' : 'Not Callable')
                        : null}
                    </div>
                  </div>
                </div>
                <div className="flow-root py-2">
                  <div className="text-md text-gray-dark float-left">Peer Visible to Members:</div>
                  <div className="float-right">
                    <ToggleButton
                      id={'peerStatus'}
                      status={listenerDetails?.peer_status === 'enabled' ? true : false}
                      setStatus={(data) => {
                        setListenerDetails((prev: any) => ({
                          ...prev,

                          peer_status: data ? 'enabled' : 'disabled',
                        }));

                        setEnableSaveOption(true);
                      }}
                    ></ToggleButton>
                  </div>
                </div>
                <div className="flow-root py-2">
                  <div className="text-md text-gray-dark float-left">Listener Can Take Calls</div>
                  <div className="float-right">
                    <ToggleButton
                      id={'listenerStatus'}
                      status={
                        listenerDetails?.status === 'enabled' &&
                        [PeerType.ACTIVE_LISTENER, PeerType.UPGRADING_PEER].includes(listenerDetails?.state)
                          ? true
                          : false
                      }
                      setStatus={(data) => {
                        setListenerDetails((prev: any) => ({
                          ...prev,

                          status: data ? 'enabled' : 'disabled',
                        }));

                        setEnableSaveOption(true);
                      }}
                      disabled={[PeerType.ONBOARDING_PEER, PeerType.REJECTED_PEER, PeerType.ACTIVE_PEER].includes(
                        listenerDetails?.state,
                      )}
                    ></ToggleButton>
                  </div>
                </div>
                <div className="flow-root py-2">
                  <div className="text-md text-gray-dark float-left">SMS Enabled</div>
                  <div className="float-right">
                    <ToggleButton
                      id={'smsEnabled'}
                      status={userDetails?.is_text_compatible_phone}
                      setStatus={(data) => {
                        setUserValue('is_text_compatible_phone', data);
                      }}
                    ></ToggleButton>
                  </div>
                </div>
                <div className="flow-root py-2">
                  <div className="text-md text-gray-dark float-left">MMS Enabled</div>
                  <div className="float-right">
                    <ToggleButton
                      id={'mmsEnabled'}
                      status={userDetails?.is_mms_compatible_phone}
                      setStatus={(data) => {
                        setUserValue('is_mms_compatible_phone', data);
                      }}
                    ></ToggleButton>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between px-7 py-2">
              <div className="w-1/2 px-2">
                <DropDown
                  Label={'Peer State'}
                  value={listenerDetails?.state}
                  setValue={(data) => {
                    setListenerDetails((prev: any) => ({
                      ...prev,
                      state: data,
                    }));
                    setEnableSaveOption(true);
                  }}
                  data-testid="peer-state"
                >
                  {Object.keys(PeerTypeLabels).map((item: string) => (
                    <option key={item} value={item}>
                      {/* @ts-ignore */}
                      {PeerTypeLabels[item]}
                    </option>
                  ))}
                </DropDown>
              </div>
              <div className="w-1/2 px-2">
                <DropDown
                  Label={'User Status'}
                  value={userDetails?.status}
                  setValue={(data) => {
                    setUserValue('status', data);
                  }}
                  data-testid="user-status"
                >
                  {Object.keys(userStatus).map((item: string) => (
                    <option key={item} value={item}>
                      {/* @ts-ignore */}
                      {userStatus[item]}
                    </option>
                  ))}
                </DropDown>
              </div>
            </div>
            <div className="flex justify-between px-7 py-2"></div>
            {listenerDetails?.state !== 'peer_rejected' ? (
              <div className="w-1/2 pl-9">
                <div className="grid grid-cols-3 gap-4">
                  {isPeerListener() && (
                    <>
                      <label className="text-gray-dark col-span-2 flex items-center leading-4">Listener Guide</label>
                      <div
                        className={`${
                          listenerDetails?.has_completed_listener_guide
                            ? 'stacked-bar-green-primary'
                            : 'stacked-bar-orange-primary'
                        } w-22 flex h-7 items-center justify-center rounded-md font-medium text-white`}
                        data-testid="listener-guide"
                      >
                        {listenerDetails?.has_completed_listener_guide ? 'Done' : 'Pending'}
                      </div>
                    </>
                  )}
                  {isPeerListener() && (
                    <>
                      <label className="text-gray-dark col-span-2 flex items-center leading-4">About Me</label>
                      <div
                        className={`${
                          aboutMeStatus === OnboardingStatus.DONE
                            ? 'stacked-bar-green-primary'
                            : 'stacked-bar-orange-primary'
                        } w-22 flex h-7 items-center justify-center rounded-md font-medium text-white`}
                        data-testid="about-me"
                      >
                        {aboutMeStatus}
                      </div>
                    </>
                  )}
                  <label className="text-gray-dark col-span-2 flex items-center leading-4">Profile Photo</label>
                  <div
                    className={`${
                      profileApprovedStatus === OnboardingStatus.DONE
                        ? 'stacked-bar-green-primary'
                        : 'stacked-bar-orange-primary'
                    } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                    data-testid="profile-photo-status"
                  >
                    {profileApprovedStatus}
                  </div>
                  <label className="text-gray-dark col-span-2 flex items-center leading-4">Display Name</label>
                  <div
                    className={`${
                      displayNameStatus === OnboardingStatus.DONE
                        ? 'stacked-bar-green-primary'
                        : 'stacked-bar-orange-primary'
                    } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                    data-testid="display-name-status"
                  >
                    {displayNameStatus}
                  </div>
                  {isPeerListener() && (
                    <>
                      <label className="text-gray-dark col-span-2 flex items-center leading-4">Listening Academy</label>
                      <div
                        className={`${
                          listenerDetails?.required_training_complete
                            ? 'stacked-bar-green-primary'
                            : 'stacked-bar-orange-primary'
                        } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                        data-testid="listening-academy-status"
                      >
                        {listenerDetails?.required_training_complete ? 'Done' : 'Pending'}
                      </div>
                    </>
                  )}
                  <label className="text-gray-dark col-span-2 flex items-center leading-4">Experiences</label>
                  <div
                    className={`${
                      isContentOptional || listenerDetails?.has_required_experiences
                        ? 'stacked-bar-green-primary'
                        : 'stacked-bar-orange-primary'
                    } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                    data-testid="experiences-status"
                  >
                    {getExperiencesChecklistLabel({
                      content_optional: isContentOptional,
                      has_required_experiences: listenerDetails?.has_required_experiences,
                    })}
                  </div>
                  {isPeerListener() && (
                    <>
                      <label className="text-gray-dark col-span-2 flex items-center leading-4">Office Hours</label>
                      <div
                        className={`${
                          listenerDetails?.has_required_office_hours
                            ? 'stacked-bar-green-primary'
                            : 'stacked-bar-orange-primary'
                        } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                        data-testid="office-hours-status"
                      >
                        {listenerDetails?.has_required_office_hours ? 'Done' : 'Pending'}
                      </div>
                      <label className="text-gray-dark col-span-2 flex items-center leading-4">Background Check</label>
                      <div
                        className={`${
                          listenerDetails?.background_check_verified
                            ? 'stacked-bar-green-primary'
                            : 'stacked-bar-orange-primary'
                        } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                        data-testid="background-check-status"
                      >
                        {listenerDetails?.background_check_verified ? 'Done' : 'Pending'}
                      </div>
                    </>
                  )}
                  <label className="text-gray-dark col-span-2 flex items-center leading-4">Payment Method</label>
                  <div
                    className={`${
                      listenerDetails?.stripe_user_id ? 'stacked-bar-green-primary' : 'stacked-bar-orange-primary'
                    } w-22 stacked-bar-orange-primary flex h-7 items-center justify-center rounded-md font-medium text-white`}
                    data-testid="payment-method-status"
                  >
                    {listenerDetails?.stripe_user_id ? 'Done' : 'Pending'}
                  </div>
                  {isPeerListener() && (
                    <>
                      <label className="text-gray-dark col-span-2 flex items-center leading-4">
                        Lobby Subscription
                      </label>
                      <ToggleButton
                        id={'lobbySubscription'}
                        status={listenerDetails?.has_lobby_subscription}
                        setStatus={(data) => {
                          setListenerDetails((prev: any) => ({
                            ...prev,

                            has_lobby_subscription: data,
                          }));

                          setEnableSaveOption(true);
                        }}
                        data-testid="lobby-subscription-toggle"
                      ></ToggleButton>
                    </>
                  )}
                </div>
              </div>
            ) : null}
            <div className="w-full px-7">
              <p className="text-gray-dark py-2 text-left font-bold">Relationships</p>
              <hr className="bg-gray-dark"></hr>
              <p className="text-gray-dark mt-2 text-sm italic">
                Domain and Client affiliations are mutually exclusive. If a client has a domain affiliation, the peer
                will inherit that affiliation.
              </p>
              <div className="mt-2 flex flex-wrap gap-4">
                <div className="min-w-[200px] flex-1">
                  <DropDown
                    Label={'Group Affiliation'}
                    value={listenerDetails?.client_id ?? ''}
                    setValue={(data) => {
                      setListenerDetails((prev: any) => ({
                        ...prev,
                        client_id: data || null,
                        domain_id: null, // Clear domain when setting group; affiliations are mutually exclusive
                      }));
                      setEnableSaveOption(true);
                    }}
                  >
                    <>
                      {isClientsFetching && <option>Loading...</option>}
                      <option value="">None</option>
                      {clients &&
                        clients.map((client: any) => (
                          <option key={`Client ${client.id}`} value={client.id}>
                            {client.name} (#{client.id})
                          </option>
                        ))}
                    </>
                  </DropDown>
                  {!isClientsFetching && contentOptionalFromClient && (
                    <span
                      className="stacked-bar-green-primary mt-2 inline-block rounded-md px-3 py-0.5 text-xs font-bold text-white"
                      data-testid="content-optional-client-badge"
                      title="Experiences are waived because this peer's group affiliation has Content Optional enabled."
                    >
                      Content optional (group)
                    </span>
                  )}
                </div>
                <div className="min-w-[200px] flex-1">
                  <DropDown
                    Label={'Domain Affiliation'}
                    value={listenerDetails?.domain_id ?? ''}
                    setValue={(data) => {
                      setListenerDetails((prev: any) => ({
                        ...prev,
                        domain_id: data || null,
                        client_id: null, // Clear group when setting domain; affiliations are mutually exclusive
                      }));
                      setEnableSaveOption(true);
                    }}
                  >
                    <>
                      {isDomainsFetching && <option>Loading...</option>}
                      <option value="">None</option>
                      {domains &&
                        domains.map((domain: any) => (
                          <option key={`Domain ${domain.id}`} value={domain.id}>
                            {domain.name} (#{domain.id})
                          </option>
                        ))}
                    </>
                  </DropDown>
                </div>
                <div className="min-w-[200px] flex-1">
                  <DropDown
                    Label={'Resource Affiliation'}
                    value={
                      allResources?.find((resource) => Number(resource.id) === Number(listenerDetails?.resource_id))
                        ? listenerDetails?.resource_id
                        : ''
                    }
                    setValue={(data) => {
                      setListenerDetails((prev: any) => ({
                        ...prev,
                        resource_id: data,
                      }));
                      setEnableSaveOption(true);
                    }}
                  >
                    <>
                      {isResourcesFetching && <option>Loading...</option>}
                      <option value="">None</option>
                      {resources && (
                        <>
                          <option disabled>GLOBAL RESOURCES:</option>
                          {resources.map((resource: any) => (
                            <option key={`Resource ${resource.id}`} value={resource.id}>
                              {resource.name} (#{resource.id})
                            </option>
                          ))}
                        </>
                      )}
                      {clientResources && listenerDetails?.client_id && (
                        <>
                          <option disabled>CLIENT RESOURCES:</option>
                          {clientResources.map((resource: any) => (
                            <option key={`Resource ${resource.id}`} value={resource.id}>
                              {resource.name} (#{resource.id})
                            </option>
                          ))}
                        </>
                      )}
                    </>
                  </DropDown>
                </div>
              </div>
            </div>
            <div className="w-full px-7">
              <div className="flex justify-between">
                <p className="text-gray-dark py-2 text-left font-bold">Profile Tags</p>
                <button
                  className="text-blue-primary py-2 text-right font-bold"
                  onClick={() => {
                    setAddTagsPopup('PROFILE');
                  }}
                >
                  + Add
                </button>
              </div>
              <hr className="bg-gray-dark"></hr>
              <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 text-sm">
                {selectedProfileTags.length > 0 ? (
                  selectedProfileTags.map((item: any, index: number) => {
                    return (
                      <button
                        key={`profile_${item.id}`}
                        className={`bg-citrus rounded-md text-sm font-bold text-white ${
                          index === 0 ? 'ml-2 mt-2' : ''
                        }`}
                      >
                        <div className="flex px-3 py-2">
                          <div className="grow pr-2">{item.tag.name}</div>
                          <div
                            className="grow-0"
                            onClick={() => {
                              setToggle(false, item.id);
                            }}
                          >
                            x
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="m-2"> No Profile Tags </p>
                )}
              </div>
            </div>
            <div className="w-full px-7">
              <div className="flex justify-between">
                <p className="text-gray-dark py-2 text-left font-bold">Background Tags</p>
              </div>
              <hr className="bg-gray-dark"></hr>
              <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 text-sm">
                {selectedBackgroundTags.length > 0 ? (
                  selectedBackgroundTags.map((item: any, index: number) => {
                    return (
                      <div className="grid grid-cols-7 gap-0.5 py-2" key={`background_${item.id}`}>
                        <div className="col-span-3">
                          <button
                            className={`${
                              item?.status === 'APPROVED' ? 'bg-citrus' : 'stacked-bar-orange-primary'
                            } rounded-md text-sm font-bold text-white ${index === 0 ? 'ml-2 mt-2' : ''} w-fit`}
                          >
                            <div className="flex px-3 py-2">
                              <div className="grow pr-2">{item.tag.name}</div>
                              <div
                                className="grow-0"
                                onClick={() => {
                                  handleDelete(item.id);
                                }}
                              >
                                x
                              </div>
                            </div>
                          </button>
                        </div>
                        <div className="col-span-3 flex w-full justify-center">
                          {item?.media?.length > 0 ? (
                            <button
                              className="w-full whitespace-nowrap"
                              onClick={() => handleImage(item.media[0]?.file_url, item.media[0]?.file_key)}
                            >
                              <p className="w-full cursor-pointer truncate underline">
                                {item.media[0]?.file_name ? item.media[0]?.file_name : item.media[0]?.file_url}
                              </p>
                            </button>
                          ) : (
                            <p>No File Found </p>
                          )}
                        </div>
                        <div className="flex justify-end">
                          <ToggleButton
                            status={item?.status === 'APPROVED' ? true : false}
                            id={item.name}
                            setStatus={(data) => {
                              handleBackGroundToggle(item.id, item.tag.id, data);
                            }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="m-2"> No Background Tags </p>
                )}
              </div>
            </div>
            <div className="w-full px-7">
              <div className="flex justify-between">
                <p className="text-gray-dark py-2 text-left font-bold">Sorting Tags</p>
                <button
                  className="text-blue-primary py-2 text-right font-bold"
                  onClick={() => {
                    setAddTagsPopup('SORTING');
                  }}
                >
                  + Add
                </button>
              </div>
              <hr className="bg-gray-dark"></hr>
              <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 text-sm">
                {selectedSortingTags.length > 0 ? (
                  selectedSortingTags.map((item: any, index: number) => {
                    return (
                      <button
                        key={`sorting_${item.id}`}
                        className={`bg-blue-primary rounded-md text-sm font-bold text-white ${
                          index === 0 ? 'ml-2 mt-2' : ''
                        }`}
                      >
                        <div className="flex px-3 py-2">
                          <div className="grow pr-2">{item.tag.name}</div>
                          <div
                            className="grow-0"
                            onClick={() => {
                              setToggle(false, item.id);
                            }}
                          >
                            x
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="m-2"> No Sorting Tags </p>
                )}
              </div>
              <div>
                <SectionLabel Label={'Internal Notes'} />
              </div>
              <div className="py-2">
                <textarea
                  value={listenerDetails?.notes}
                  className="text-gray-dark bg-gray-background-light h-28 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
                  onChange={(evt: any) => {
                    setListenerDetails((prev: any) => ({
                      ...prev,
                      notes: evt.target.value,
                    }));
                    setEnableSaveOption(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <Modal
          isModel={addTagsPopup?.length > 0}
          InnerComponent={
            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="mb-3 text-lg font-bold leading-6 text-gray-900" id="modal-title">
                      Add Listener Tags
                    </h3>
                    <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 text-sm">
                      {addTagsPopup &&
                        (getSelectedTagOptions(addTagsPopup) || []).map((item: any, index: number) => {
                          return (
                            <button
                              key={`addtag_${item.id}`}
                              className={`${
                                item.selected ? 'bg-citrus' : 'bg-gray-background-light'
                              } rounded-md text-sm font-bold text-white ${index === 0 ? 'ml-2 mt-2' : ' '}`}
                              onClick={() => {
                                setToggle(!item.selected, item.id);
                              }}
                            >
                              <div className="flex px-3 py-2">
                                <div className="grow pr-2">{item.name}</div>
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:justify-center sm:px-6">
                <button
                  type="button"
                  className={'rounded-full bg-gray-400 px-5 py-1 font-medium text-white'}
                  onClick={() => {
                    setAddTagsPopup('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userDataProp: state.Listeners?.listenersDetails?.data || null,
    pending: state.Listeners?.pending || false,
    comms: state.Comms.CommsList?.data || null,
    tiersList: state.tiers.TiersList?.data || null,
    listenersTags: state.Listeners.listenersTags?.data || null,
    tags: state.tags.TagsGroupList?.data || null,
  };
};

export default connect(mapStateToProps)(Profile);

const getUrlFileType = (url: string) => {
  const u = new URL(url);
  const ext: any = u.pathname.split('.').pop();
  return ext === '/' ? undefined : mime?.getType(ext.toLowerCase());
};

const handleImage = async (file_url: string, key: string) => {
  if (key === 'VERIFICATION') {
    const authenticateToken = localStorage.getItem('authorization_token');
    const response: any = await axios.get(file_url, {
      responseType: 'arraybuffer',
      headers: {
        Authorization: 'Bearer ' + authenticateToken,
        'X-Requested-With': 'XMLHTTPRequest',
        Accept: 'application/json',
      },
    });
    const blob: any = new Blob([response.data], {
      type: getUrlFileType(file_url),
    });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  } else {
    window.open(file_url, '_blank');
  }
};
const calculateApprovalStatus = (field: boolean, proposed_field: boolean, field_rejected: boolean) => {
  let status = OnboardingStatus.ERROR;
  if (field_rejected && !field) {
    status = OnboardingStatus.REVISIT;
  } else if (!field || (proposed_field && !field_rejected)) {
    status = OnboardingStatus.PENDING;
  } else if (field) {
    status = OnboardingStatus.DONE;
  }
  return status;
};

const calculateProfileImageStatus = (userDetails: any) => {
  const listenerRole = userDetails?.listener_role;
  const currentStatus = listenerRole?.current_profile_photo?.status;
  const proposedStatus = listenerRole?.proposed_profile_photo?.status;

  if (proposedStatus === 'pending') {
    return OnboardingStatus.PENDING;
  }

  if (currentStatus === 'approved') {
    return OnboardingStatus.DONE;
  }

  if (proposedStatus === 'rejected') {
    return OnboardingStatus.REVISIT;
  }

  if (!listenerRole?.current_profile_photo && !listenerRole?.proposed_profile_photo) {
    return OnboardingStatus.PENDING;
  }

  return OnboardingStatus.ERROR;
};
